import React, { useState, useEffect } from 'react';
import apiClient from '../../../../apiClient';
import { peoplePulseDataService } from '../utils/peoplePulseDataService';
import OneOnOneCalendar from './OneOnOneCalendar';
import '../styles/OneOnOneMeetings.css';
import '../styles/ViewToggle.css';

function OneOnOneMeetings() {
  const [meetings, setMeetings] = useState([]);
  const [filteredMeetings, setFilteredMeetings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedMeeting, setSelectedMeeting] = useState(null);

  const [meetingFormData, setMeetingFormData] = useState({
    participantId: '',
    scheduledDate: '',
    duration: 30,
    agenda: [{ topic: '', notes: '' }],
    location: 'virtual'
  });

  const [filters, setFilters] = useState({
    search: '',
    status: '',
    dateRange: 'upcoming'
  });

  const [noteText, setNoteText] = useState('');
  const [actionItemText, setActionItemText] = useState('');
  const [view, setView] = useState('calendar'); // 'list' or 'calendar'

  useEffect(() => {
    loadMeetings();
  }, []);

  useEffect(() => {
    applyFilters();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meetings, filters]);

  const loadMeetings = async () => {
    try {
      setLoading(true);
      setError(null);

      // Calculate date range: one month ago to one month ahead
      const startDate = new Date();
      startDate.setMonth(startDate.getMonth() - 1); // One month ago
      startDate.setHours(0, 0, 0, 0);

      const endDate = new Date();
      endDate.setMonth(endDate.getMonth() + 1); // One month ahead
      endDate.setHours(23, 59, 59, 999);

      const response = await apiClient.get('/calendar/events', {
        params: {
          startDate: startDate.toISOString(),
          endDate: endDate.toISOString()
        }
      });

      if (response.data.success) {
        // Map calendar events to meeting format
        const calendarEvents = response.data.events || [];
        const mappedMeetings = calendarEvents.map(event => ({
          id: event.id,
          participantName: event.organizer || 'Unknown',
          scheduledDate: event.start,
          duration: event.duration || 30,
          status: event.showAs === 'busy' ? 'scheduled' : 'completed',
          agenda: event.subject ? [{ topic: event.subject, notes: event.bodyPreview || '' }] : [],
          location: event.location || 'virtual',
          notes: [],
          actionItems: []
        }));
        setMeetings(mappedMeetings);
      } else {
        setError(response.data.error || 'Failed to fetch calendar events');
      }
    } catch (err) {
      console.error('Failed to load meetings:', err);
      setError('Failed to load calendar events: ' + (err.response?.data?.error || err.message));
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...meetings];

    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      filtered = filtered.filter(meeting =>
        meeting.participantName?.toLowerCase().includes(searchLower) ||
        meeting.agenda?.some(item => item.topic?.toLowerCase().includes(searchLower))
      );
    }

    if (filters.status) {
      filtered = filtered.filter(meeting => meeting.status === filters.status);
    }

    if (filters.dateRange === 'upcoming') {
      filtered = filtered.filter(meeting => new Date(meeting.scheduledDate) >= new Date());
    } else if (filters.dateRange === 'past') {
      filtered = filtered.filter(meeting => new Date(meeting.scheduledDate) < new Date());
    }

    setFilteredMeetings(filtered);
  };

  const handleCreateMeeting = async (e) => {
    e.preventDefault();
    try {
      // Construct event data for Graph API
      const eventData = {
        subject: `1-on-1 with ${meetingFormData.participantId}`,
        start: new Date(meetingFormData.scheduledDate).toISOString(),
        end: new Date(new Date(meetingFormData.scheduledDate).getTime() + meetingFormData.duration * 60000).toISOString(),
        location: meetingFormData.location,
        body: `Agenda:\n${meetingFormData.agenda.map(item => `- ${item.topic}: ${item.notes}`).join('\n')}`,
        attendees: [
          {
            emailAddress: {
              address: meetingFormData.participantId, // Assuming participantId is email
              name: meetingFormData.participantId
            },
            type: 'required'
          }
        ]
      };

      const response = await apiClient.post('/calendar/events', eventData);

      if (response.data.success) {
        // Refresh list
        loadMeetings();
        setShowCreateModal(false);
        resetForm();
        setSuccessMessage('Meeting scheduled successfully!');
        setTimeout(() => setSuccessMessage(''), 3000);
      } else {
        setError('Failed to create meeting: ' + response.data.error);
      }
    } catch (err) {
      setError('Failed to create meeting: ' + (err.response?.data?.error || err.message));
    }
  };

  const handleAddNote = async (meetingId) => {
    if (!noteText.trim()) return;

    try {
      await peoplePulseDataService.addOneOnOneNote(meetingId, { content: noteText });
      setNoteText('');
      setSuccessMessage('Note added!');
      setTimeout(() => setSuccessMessage(''), 2000);
      // Reload meeting details
      if (selectedMeeting?.id === meetingId) {
        const updated = await peoplePulseDataService.getOneOnOneById(meetingId);
        setSelectedMeeting(updated);
      }
    } catch (err) {
      setError('Failed to add note: ' + err.message);
    }
  };

  const handleAddActionItem = async (meetingId) => {
    if (!actionItemText.trim()) return;

    try {
      await peoplePulseDataService.addActionItem(meetingId, {
        description: actionItemText,
        dueDate: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
      });
      setActionItemText('');
      setSuccessMessage('Action item added!');
      setTimeout(() => setSuccessMessage(''), 2000);
      // Reload meeting details
      if (selectedMeeting?.id === meetingId) {
        const updated = await peoplePulseDataService.getOneOnOneById(meetingId);
        setSelectedMeeting(updated);
      }
    } catch (err) {
      setError('Failed to add action item: ' + err.message);
    }
  };

  const handleCompleteMeeting = async (meetingId) => {
    try {
      const updated = await peoplePulseDataService.updateOneOnOne(meetingId, { status: 'completed' });
      setMeetings(meetings.map(m => m.id === meetingId ? updated : m));
      setSuccessMessage('Meeting marked as completed!');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to complete meeting: ' + err.message);
    }
  };

  const handleCancelMeeting = async (meetingId) => {
    if (!window.confirm('Are you sure you want to cancel this meeting?')) return;

    try {
      await peoplePulseDataService.updateOneOnOne(meetingId, { status: 'cancelled' });
      setMeetings(meetings.map(m => m.id === meetingId ? { ...m, status: 'cancelled' } : m));
      setSuccessMessage('Meeting cancelled');
      setTimeout(() => setSuccessMessage(''), 2000);
    } catch (err) {
      setError('Failed to cancel meeting: ' + err.message);
    }
  };

  const addAgendaItem = () => {
    setMeetingFormData({
      ...meetingFormData,
      agenda: [...meetingFormData.agenda, { topic: '', notes: '' }]
    });
  };

  const removeAgendaItem = (index) => {
    const newAgenda = meetingFormData.agenda.filter((_, i) => i !== index);
    setMeetingFormData({ ...meetingFormData, agenda: newAgenda });
  };

  const resetForm = () => {
    setMeetingFormData({
      participantId: '',
      scheduledDate: '',
      duration: 30,
      agenda: [{ topic: '', notes: '' }],
      location: 'virtual'
    });
  };

  const openMeetingDetails = async (meeting) => {
    try {
      setSelectedMeeting(meeting);
      setShowDetailsModal(true);
    } catch (err) {
      console.error('Failed to load meeting details:', err);
      setError('Failed to load meeting details: ' + err.message);
    }
  };

  const handleDateClick = (date) => {
    // Pre-fill the form with the selected date
    const dateTimeString = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 9, 0)
      .toISOString()
      .slice(0, 16);
    setMeetingFormData({
      ...meetingFormData,
      scheduledDate: dateTimeString
    });
    setShowCreateModal(true);
  };

  const getStatusColor = (status) => {
    const colors = {
      'scheduled': '#3B82F6',
      'completed': '#10B981',
      'cancelled': '#EF4444',
      'in-progress': '#F59E0B'
    };
    return colors[status] || '#6C757D';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Los_Angeles' // US Pacific Time (PST/PDT)
    });
  };

  if (loading) {
    return (
      <div className="hr-dashboard-loading">
        <div className="loading-spinner"></div>
        <p>Loading meetings...</p>
      </div>
    );
  }

  return (
    <div className="hr-dashboard-container peoplepulse-page" style={{ marginTop: '80px' }}>
      <div
        style={{
          fontFamily: "Inter, sans-serif",
          fontSize: "1.4rem",
          fontWeight: "600",
          color: "#2563EB", // Primary blue shade
          margin: "20px 20px",
          paddingTop: '20px',
          textAlign: "left"
        }}
      >
        This calendar displays your Outlook meetings and allows you to create new ones directly.
      </div>

      {error && (
        <div className="alert alert-error">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success">
          <span>{successMessage}</span>
        </div>
      )}

      <div className="controls-container" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px', padding: '0 20px' }}>
        <div className="view-toggle">
          <button
            className={`toggle-btn ${view === 'calendar' ? 'active' : ''}`}
            onClick={() => setView('calendar')}
            style={{ padding: '8px 16px', marginRight: '10px', borderRadius: '20px', border: '1px solid #e5e7eb', background: view === 'calendar' ? '#2563EB' : 'white', color: view === 'calendar' ? 'white' : '#374151', cursor: 'pointer' }}
          >
            Calendar
          </button>
          <button
            className={`toggle-btn ${view === 'list' ? 'active' : ''}`}
            onClick={() => setView('list')}
            style={{ padding: '8px 16px', borderRadius: '20px', border: '1px solid #e5e7eb', background: view === 'list' ? '#2563EB' : 'white', color: view === 'list' ? 'white' : '#374151', cursor: 'pointer' }}
          >
            List
          </button>
        </div>

        <button className="btn-primary" onClick={() => setShowCreateModal(true)}>
          + Schedule Meeting
        </button>
      </div>

      <div className="filters-bar">
        <input
          type="text"
          placeholder="Search meetings..."
          value={filters.search}
          onChange={(e) => setFilters({ ...filters, search: e.target.value })}
          className="search-input"
        />

        <select value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
          <option value="">All Statuses</option>
          <option value="scheduled">Scheduled</option>
          <option value="completed">Completed</option>
          <option value="cancelled">Cancelled</option>
        </select>

        <select value={filters.dateRange} onChange={(e) => setFilters({ ...filters, dateRange: e.target.value })}>
          <option value="all">All Time</option>
          <option value="upcoming">Upcoming</option>
          <option value="past">Past Meetings</option>
        </select>

        <button className="btn-secondary" onClick={() => setFilters({ search: '', status: '', dateRange: 'upcoming' })}>
          Clear Filters
        </button>
      </div>

      {/* Calendar View */}
      {view === 'calendar' ? (
        <OneOnOneCalendar
          meetings={filteredMeetings}
          onMeetingClick={openMeetingDetails}
          onDateClick={handleDateClick}
        />
      ) : (
        <div className="meetings-list-view" style={{ padding: '20px' }}>
          {filteredMeetings.length === 0 ? (
            <div className="no-meetings" style={{ textAlign: 'center', padding: '40px', color: '#6B7280' }}>No meetings found matching your filters.</div>
          ) : (
            <div className="meetings-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: '20px' }}>
              {filteredMeetings.map(meeting => (
                <div key={meeting.id} className="meeting-card" onClick={() => openMeetingDetails(meeting)} style={{ border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', cursor: 'pointer', backgroundColor: 'white', boxShadow: '0 1px 3px rgba(0,0,0,0.1)' }}>
                  <div className="meeting-header" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '10px' }}>
                    <span className="meeting-date" style={{ fontSize: '0.875rem', color: '#6B7280' }}>{formatDate(meeting.scheduledDate)}</span>
                    <span className="status-badge" style={{ padding: '2px 8px', borderRadius: '12px', fontSize: '0.75rem', backgroundColor: getStatusColor(meeting.status), color: 'white' }}>{meeting.status}</span>
                  </div>
                  <h3 style={{ margin: '0 0 10px 0', fontSize: '1.125rem' }}>{meeting.participantName}</h3>
                  <p className="meeting-location" style={{ margin: '0 0 10px 0', color: '#4B5563' }}>📍 {meeting.location}</p>
                  {meeting.agenda && meeting.agenda.length > 0 && (
                    <p className="meeting-agenda-preview" style={{ margin: '0', fontSize: '0.875rem', color: '#6B7280' }}>
                      📝 {meeting.agenda[0].topic}
                      {meeting.agenda.length > 1 && ` +${meeting.agenda.length - 1} more`}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Create Meeting Modal */}
      {showCreateModal && (
        <div className="modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Schedule 1-on-1 Meeting</h2>
              <button onClick={() => { setShowCreateModal(false); resetForm(); }}>×</button>
            </div>

            <form onSubmit={handleCreateMeeting}>
              <div className="form-group">
                <label>Participant *</label>
                <input
                  type="text"
                  value={meetingFormData.participantId}
                  onChange={(e) => setMeetingFormData({ ...meetingFormData, participantId: e.target.value })}
                  required
                  placeholder="Enter participant name or email"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Date & Time *</label>
                  <input
                    type="datetime-local"
                    value={meetingFormData.scheduledDate}
                    onChange={(e) => setMeetingFormData({ ...meetingFormData, scheduledDate: e.target.value })}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>Duration (minutes) *</label>
                  <select
                    value={meetingFormData.duration}
                    onChange={(e) => setMeetingFormData({ ...meetingFormData, duration: parseInt(e.target.value) })}
                    required
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>Location</label>
                <select
                  value={meetingFormData.location}
                  onChange={(e) => setMeetingFormData({ ...meetingFormData, location: e.target.value })}
                >
                  <option value="virtual">Virtual (Video Call)</option>
                  <option value="office">Office</option>
                  <option value="offsite">Offsite</option>
                </select>
              </div>

              <div className="agenda-section">
                <div className="section-header">
                  <h3>Agenda Items</h3>
                  <button type="button" className="btn-secondary small" onClick={addAgendaItem}>
                    + Add Item
                  </button>
                </div>

                {meetingFormData.agenda.map((item, index) => (
                  <div key={index} className="agenda-item-form">
                    <div className="form-group">
                      <label>Topic *</label>
                      <input
                        type="text"
                        value={item.topic}
                        onChange={(e) => {
                          const newAgenda = [...meetingFormData.agenda];
                          newAgenda[index].topic = e.target.value;
                          setMeetingFormData({ ...meetingFormData, agenda: newAgenda });
                        }}
                        required
                        placeholder="e.g., Career development discussion"
                      />
                    </div>

                    <div className="form-group">
                      <label>Notes (Optional)</label>
                      <textarea
                        value={item.notes}
                        onChange={(e) => {
                          const newAgenda = [...meetingFormData.agenda];
                          newAgenda[index].notes = e.target.value;
                          setMeetingFormData({ ...meetingFormData, agenda: newAgenda });
                        }}
                        rows="2"
                        placeholder="Prep notes or talking points..."
                      />
                    </div>

                    <button
                      type="button"
                      className="btn-danger small"
                      onClick={() => removeAgendaItem(index)}
                      disabled={meetingFormData.agenda.length === 1}
                    >
                      Remove
                    </button>
                  </div>
                ))}
              </div>

              <div className="modal-actions">
                <button type="button" className="btn-secondary" onClick={() => { setShowCreateModal(false); resetForm(); }}>
                  Cancel
                </button>
                <button type="submit" className="btn-primary">
                  Schedule Meeting
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Meeting Details Modal */}
      {showDetailsModal && selectedMeeting && (
        <div className="modal-overlay" onClick={() => setShowDetailsModal(false)}>
          <div className="modal-content large-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Meeting with {selectedMeeting.participantName}</h2>
              <button onClick={() => setShowDetailsModal(false)}>×</button>
            </div>

            <div className="meeting-details" style={{ paddingInline: '30px' }}>
              <div className="detail-section">
                <h3>Meeting Info</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Date & Time</label>
                    <span>{formatDate(selectedMeeting.scheduledDate)}</span>
                  </div>
                  <div className="detail-item">
                    <label>Duration</label>
                    <span>{selectedMeeting.duration} minutes</span>
                  </div>
                  <div className="detail-item">
                    <label>Location</label>
                    <span>{selectedMeeting.location}</span>
                  </div>
                  <div className="detail-item">
                    <label>Status</label>
                    <span className="badge" style={{ backgroundColor: getStatusColor(selectedMeeting.status) }}>
                      {selectedMeeting.status}
                    </span>
                  </div>
                </div>
              </div>

              <div className="detail-section">
                <h3>Agenda</h3>
                {selectedMeeting.agenda && selectedMeeting.agenda.length > 0 ? (
                  selectedMeeting.agenda.map((item, index) => (
                    <div key={index} className="agenda-item-detail">
                      <h4>{item.topic}</h4>
                      {item.notes && <p>{item.notes}</p>}
                    </div>
                  ))
                ) : (
                  <p>No agenda items</p>
                )}
              </div>

              <div className="detail-section">
                <h3>Notes</h3>
                <div className="add-note-form" style={{ marginBottom: '15px' }}>
                  <textarea
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                    placeholder="Add a private note..."
                    rows="2"
                    style={{ width: '100%', marginBottom: '10px', padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                  <button className="btn-secondary small" onClick={() => handleAddNote(selectedMeeting.id)}>Add Note</button>
                </div>
                {selectedMeeting.notes && selectedMeeting.notes.length > 0 ? (
                  selectedMeeting.notes.map((note, i) => (
                    <div key={i} className="note-item" style={{ backgroundColor: '#f3f4f6', padding: '10px', borderRadius: '4px', marginBottom: '8px' }}>
                      <p style={{ margin: '0 0 5px 0' }}>{note.content}</p>
                      <small style={{ color: '#6b7280' }}>{new Date(note.createdAt || Date.now()).toLocaleDateString()}</small>
                    </div>
                  ))
                ) : <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No notes yet</p>}
              </div>

              <div className="detail-section">
                <h3>Action Items</h3>
                <div className="add-action-form" style={{ marginBottom: '15px', display: 'flex', gap: '10px' }}>
                  <input
                    type="text"
                    value={actionItemText}
                    onChange={(e) => setActionItemText(e.target.value)}
                    placeholder="New action item..."
                    style={{ flex: 1, padding: '8px', borderRadius: '4px', border: '1px solid #d1d5db' }}
                  />
                  <button className="btn-secondary small" onClick={() => handleAddActionItem(selectedMeeting.id)}>Add</button>
                </div>
                {selectedMeeting.actionItems && selectedMeeting.actionItems.length > 0 ? (
                  selectedMeeting.actionItems.map((item, i) => (
                    <div key={i} className="action-item" style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' }}>
                      <input type="checkbox" checked={item.completed} readOnly />
                      <span>{item.description}</span>
                    </div>
                  ))
                ) : <p style={{ color: '#9ca3af', fontStyle: 'italic' }}>No action items yet</p>}
              </div>
            </div>

            <div className="modal-actions" style={{ justifyContent: 'space-between' }}>
              <div style={{ display: 'flex', gap: '10px' }}>
                {selectedMeeting.status !== 'completed' && selectedMeeting.status !== 'cancelled' && (
                  <>
                    <button className="btn-danger" onClick={() => handleCancelMeeting(selectedMeeting.id)}>Cancel Meeting</button>
                    <button className="btn-success" onClick={() => handleCompleteMeeting(selectedMeeting.id)} style={{ backgroundColor: '#10B981', color: 'white', border: 'none', padding: '8px 16px', borderRadius: '4px', cursor: 'pointer' }}>Mark Complete</button>
                  </>
                )}
              </div>
              <button className="btn-secondary" onClick={() => setShowDetailsModal(false)}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default OneOnOneMeetings;

